import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Amount } from "@/components/shared/Amount";
import { AHeader, ACard, ATable, inputDark } from "@/components/shared/AdminKit";
import { StatusBadge } from "@/components/shared/Badges";
import { TimeAgo } from "@/components/shared/TimeAgo";
import { fmtDateShort } from "@/components/shared/DashKit";
import { inactivity } from "@/lib/inactivity";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Search, Ban, CheckCircle2, Wallet, Trash2 } from "lucide-react";

const InactivityBadge = ({ lastLogin, createdAt }) => {
  const i = inactivity(lastLogin, createdAt);
  const cls = i.neverLoggedIn ? "bg-slate-700/40 text-slate-300" : i.inactive ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400";
  return <span className={`whitespace-nowrap rounded px-2 py-0.5 text-xs ${cls}`}>{i.label}</span>;
};

export default function AdminUsers() {
  const { t } = useContent();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [blockers, setBlockers] = useState(null);

  const load = () => { setLoading(true); api.get(`/admin/users${search ? `?search=${search}` : ""}`).then((r) => { setUsers(r.data); setLoading(false); }); };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  const toggle = async (u) => {
    const status = u.status === "active" ? "suspended" : "active";
    await api.put(`/admin/users/${u.id}/status`, { status }); toast.success(t("admin.common.status_updated", "Statut mis à jour")); load();
  };

  const del = async () => {
    if (!confirmDel) return;
    setDeleting(true); setBlockers(null);
    try {
      await api.delete(`/admin/users/${confirmDel.id}`);
      toast.success(t("admin.users.delete.success", "Compte supprimé avec succès."));
      setConfirmDel(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.blockers) { setBlockers(d.blockers); }
      else { toast.error((typeof d === "string" ? d : null) || t("admin.users.delete.failed", "Échec de la suppression")); }
    } finally { setDeleting(false); }
  };
  const blockerLabel = (code) => t(`admin.users.delete.blocker.${code}`, code);

  return (
    <div>
      <AHeader title={t("admin.users.title", "Utilisateurs")} subtitle={t("admin.users.subtitle", "Gérez les comptes clients.")} />
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-3 lg:w-96">
        <Search className="h-4 w-4 text-slate-500" />
        <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("admin.users.search", "Rechercher par nom ou email...")} className="w-full bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-slate-500" />
      </div>
      <ACard>
        {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : (
            <ATable head={[t("admin.users.col.name", "Nom"), t("admin.users.col.email", "Email"), t("admin.users.col.role", "Rôle"), t("admin.users.col.balance", "Solde"), t("admin.users.col.orders", "Commandes"), t("admin.users.col.status", "Statut"), t("admin.users.col.activity", "Activité"), t("admin.users.col.registered", "Inscrit"), t("admin.users.col.actions", "Actions")]}>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-800/60 last:border-0" data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-3 font-medium">{u.name}</td>
                  <td className="px-3 py-3 text-slate-400">{u.email}</td>
                  <td className="px-3 py-3"><span className="rounded bg-slate-800 px-2 py-0.5 text-xs capitalize">{u.role}</span></td>
                  <td className="px-3 py-3"><Amount value={u.balance} /></td>
                  <td className="px-3 py-3">{u.orders_count}</td>
                  <td className="px-3 py-3"><StatusBadge status={u.status} /></td>
                  <td className="px-3 py-3" data-testid={`user-activity-${u.email}`}><InactivityBadge lastLogin={u.last_login} createdAt={u.created_at} /></td>
                  <td className="px-3 py-3 text-slate-400">{fmtDateShort(u.created_at)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setDetail(u.id)} data-testid={`view-user-${u.email}`} className="text-accent hover:underline text-xs">{t("admin.users.action.view", "Voir")}</button>
                      <button onClick={() => setAdjust(u)} data-testid={`adjust-user-${u.email}`} className="text-slate-400 hover:text-white"><Wallet className="h-4 w-4" /></button>
                      {u.role === "customer" && <button onClick={() => toggle(u)} data-testid={`toggle-user-${u.email}`} className={u.status === "active" ? "text-red-400" : "text-emerald-400"}>{u.status === "active" ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}</button>}
                      {u.role !== "super_admin" && u.status !== "deleted" && <button onClick={() => { setBlockers(null); setConfirmDel(u); }} data-testid={`delete-user-${u.email}`} title={t("admin.users.action.delete_account", "Supprimer le compte")} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </ATable>
          )}
      </ACard>
      {detail && <UserDetail id={detail} onClose={() => setDetail(null)} />}
      {adjust && <AdjustModal user={adjust} onClose={() => setAdjust(null)} onDone={load} />}

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => { if (!v && !deleting) { setConfirmDel(null); setBlockers(null); } }}>
        <AlertDialogContent className="bg-slate-900 text-white border-slate-800" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="delete-user-title">{blockers ? t("admin.users.delete.blocked_title", "Suppression impossible") : t("admin.users.delete.title", "Voulez-vous vraiment supprimer ce compte ?")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400" asChild>
              <div>
                {!blockers && <div>{t("admin.users.delete.desc", "Cette action est irréversible. Le compte sera désactivé/supprimé et l'accès sera révoqué.")}</div>}
                {confirmDel && (
                  <div className="mt-2 text-sm text-white">
                    <span className="font-medium">{confirmDel.name}</span> ({confirmDel.email})
                    <span className="ml-2 rounded bg-slate-800 px-2 py-0.5 text-xs capitalize text-slate-300">{t("admin.users.delete.account_type", "Type de compte :")} {confirmDel.role}</span>
                  </div>
                )}
                {blockers && (
                  <div className="mt-3" data-testid="delete-user-blockers">
                    <div className="text-sm text-amber-300">{t("admin.users.delete.blocked_intro", "Résolvez ces éléments avant de supprimer :")}</div>
                    <ul className="mt-1 list-disc pl-5 text-sm text-slate-300">
                      {blockers.map((b, i) => (
                        <li key={i} data-testid={`blocker-${b.code}`}>{blockerLabel(b.code)}{b.count > 1 ? ` (${b.count})` : ""}{b.detail != null ? ` — ${b.detail}` : ""}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel" disabled={deleting} className="border-slate-700 bg-transparent text-white hover:bg-slate-800">{t("admin.users.delete.cancel", "Annuler")}</AlertDialogCancel>
            {!blockers && (
              <AlertDialogAction data-testid="delete-user-confirm" onClick={(e) => { e.preventDefault(); del(); }} disabled={deleting} className="bg-red-600 text-white hover:bg-red-700">
                {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("admin.users.deleting", "Suppression...")}</> : t("admin.users.delete.confirm", "Oui, supprimer")}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserDetail({ id, onClose }) {
  const { t } = useContent();
  const [u, setU] = useState(null);
  useEffect(() => { api.get(`/admin/users/${id}`).then((r) => setU(r.data)); }, [id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-slate-900 text-white border-slate-800" data-testid="user-detail-modal">
        <DialogHeader><DialogTitle className="font-display">{u?.name}</DialogTitle></DialogHeader>
        {!u ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-slate-800 p-3"><div className="text-slate-500">{t("admin.users.col.balance", "Solde")}</div><div className="font-bold"><Amount value={u.balance} /></div></div>
              <div className="rounded-lg border border-slate-800 p-3"><div className="text-slate-500">{t("admin.users.col.orders", "Commandes")}</div><div className="font-bold">{u.orders?.length}</div></div>
              <div className="rounded-lg border border-slate-800 p-3"><div className="text-slate-500">{t("admin.users.col.status", "Statut")}</div><StatusBadge status={u.status} /></div>
            </div>
            <div>
              <div className="mb-2 font-medium text-slate-400">{t("admin.users.detail.recent_orders", "Dernières commandes")}</div>
              <div className="max-h-48 space-y-1 overflow-auto">
                {(u.orders || []).slice(0, 10).map((o) => (
                  <div key={o.id} className="flex justify-between rounded border border-slate-800 px-3 py-2 text-xs">
                    <span>#{o.order_number} · {o.service_name}</span><span><Amount value={o.price} /> · {o.status}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 font-medium text-slate-400">{t("admin.users.detail.login_history", "Historique des connexions")}</div>
              <div className="max-h-48 space-y-1 overflow-auto" data-testid="user-login-history">
                {(u.logins || []).map((l, i) => (
                  <div key={i} className="flex items-center justify-between rounded border border-slate-800 px-3 py-2 text-xs">
                    <TimeAgo ts={l.created_at} className="text-slate-200" />
                    <span className="text-slate-500">{l.ip || "—"}{l.metadata?.via === "2fa" ? " · 2FA" : ""}</span>
                  </div>
                ))}
                {!(u.logins || []).length && <div className="text-xs text-slate-500">{t("admin.users.detail.no_logins", "Aucune connexion enregistrée.")}</div>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdjustModal({ user, onClose, onDone }) {
  const { t } = useContent();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { await api.post(`/admin/users/${user.id}/adjust`, { amount: Number(amount), reason: reason || t("admin.users.adjust.default_reason", "Ajustement administrateur"), reference }); toast.success(t("admin.users.adjust.done", "Ajustement effectué")); onClose(); onDone(); }
    catch { toast.error(t("admin.common.failed", "Échec")); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 text-white border-slate-800" data-testid="adjust-modal">
        <DialogHeader><DialogTitle className="font-display">{t("admin.users.adjust.title", "Ajuster le solde — ")}{user.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adjust-amount" placeholder={t("admin.users.adjust.amount_ph", "Montant (+ crédit / - débit)")} className={inputDark} />
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason" placeholder={t("admin.users.adjust.reason_ph", "Motif (obligatoire pour l'audit)")} className={inputDark} />
          <input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="adjust-reference" placeholder={t("admin.users.adjust.reference_ph", "Référence (optionnel)")} className={inputDark} />
          <button onClick={submit} disabled={loading || !amount} data-testid="submit-adjust-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-accent py-2.5 text-sm font-medium text-white disabled:opacity-50">{loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("admin.users.adjust.confirm", "Confirmer (crée une transaction + audit)")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
